import React, { useRef, useState } from "react";

import {
	RichTextInput,
	RichTextInputToolbar,
	LevelSelect,
	FormatButtons,
	ListButtons,
	LinkButtons,
	ClearButtons,
	QuoteButtons,
	ImageButtons,
	DefaultEditorOptions,
	useTiptapEditor,
} from "ra-input-rich-text";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { firebaseApp } from "../config/firebaseAuthProvider";
import { useNotify, useResourceContext } from "react-admin";
import { v4 as uuidv4 } from "uuid";
import { ToggleButton } from "@mui/material";
import { Html, UploadFile, YouTube } from "@mui/icons-material";
import { Small } from "./rich-small-block";

import { basicSetup } from "codemirror";
import { EditorState } from "@codemirror/state";
import { EditorView, drawSelection, keymap } from "@codemirror/view";
import { html } from "@codemirror/lang-html";
import { indentWithTab } from "@codemirror/commands";
import prettier from "prettier";
import parserHtml from "prettier/esm/parser-html.mjs";
import { Iframe } from "./rich-iframe";
import "./rich-text-input.css";

const uploadImage = (resource, file) => {
	const storage = getStorage(firebaseApp);
	const storageRef = ref(storage, resource + "/content/" + file.name + "-" + uuidv4());

	return new Promise((resolve, reject) => {
		uploadBytes(storageRef, file)
			.then((snapshot) => getDownloadURL(snapshot.ref))
			.then((url) => {
				resolve(url);
			})
			.catch((e) => {
				reject(e);
			});
	});
};

const uploadAndInsertImage = (resource, file, notify, editor, view, position) => {
	uploadImage(resource, file)
		.then(function (url) {
			// Preload
			let uploadedImage = new Image();
			uploadedImage.src = url;
			uploadedImage.onload = function () {
				if (view && position) {
					// Insert at position
					const { schema } = view.state;
					const coordinates = view.posAtCoords({ left: position.x, top: position.y });
					const node = schema.nodes.image.create({ src: url }); // creates the image element
					const transaction = view.state.tr.insert(coordinates.pos, node); // places it in the correct position
					return view.dispatch(transaction);
				} else {
					// Insert at the end
					editor.chain().focus().setImage({ src: url }).run();
				}
			};

			notify(`L'image est bien enregistrée`, { type: "success" });
		})
		.catch(function (error) {
			console.log(error);
			notify(
				`L'enregistrement de l'image a rencontré un problème. Veuillez contacter l'administrateur`,
				{ type: "error" }
			);
		});
};

const UploadImageButtons = ({ label = "Fichier", ...props }) => {
	const editor = useTiptapEditor();
	const resource = useResourceContext();
	const notify = useNotify();

	const onClick = () => {
		// Open file selector
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "image/*";
		input.onchange = (e) => {
			var file = e.target.files[0];

			if (!file) {
				return;
			}

			uploadAndInsertImage(resource, file, notify, editor);
		};
		input.click();
	};

	return (
		<ToggleButton
			aria-label={label}
			title={label}
			{...props}
			disabled={!editor?.isEditable}
			value="image"
			onClick={onClick}
		>
			<UploadFile fontSize="inherit" />
		</ToggleButton>
	);
};

const SmallButtons = ({ label = "Small", ...props }) => {
	const editor = useTiptapEditor();
	const resource = useResourceContext();

	const onClick = () => {
		editor.chain().focus().toggleSmall().run();
	};

	return (
		<ToggleButton
			aria-label={label}
			title={label}
			{...props}
			disabled={!editor?.isEditable}
			value="small"
			onClick={onClick}
		>
			<b style={{ fontSize: "inherit", lineHeight: "1.0", textTransform: "lowercase" }}>Small</b>
		</ToggleButton>
	);
};

const transformYoutubeUrlToEmbed = (url) => {
	// if this format: https://youtu.be/wXHili6gRvs
	if (url.includes("youtu.be/")) {
		return url.replace("youtu.be/", "youtube.com/embed/");
	}

	// if this format: https://www.youtube.com/watch?v=wXHili6gRvs
	if (url.includes("/watch?v=")) {
		return url.replace("/watch?v=", "/embed/");
	}

	return url;
};

const VideoButtons = ({ label = "Video", ...props }) => {
	const editor = useTiptapEditor();
	const resource = useResourceContext();

	const onClick = () => {
		const url = window.prompt("URL de la vidéo :");

		if (url) {
			editor.chain().focus().setIframe({ src: transformYoutubeUrlToEmbed(url) }).run();
		}
	};

	return (
		<ToggleButton
			aria-label={label}
			title={label}
			{...props}
			disabled={!editor?.isEditable}
			value="small"
			onClick={onClick}
		>
			<YouTube fontSize="inherit" />
		</ToggleButton>
	);
};

const CodeViewButtons = ({
	label = "Small",
	isActivated,
	setIsActivated,
	codeMirrorInstance,
	setCodeMirrorInstance,
	textAreaRef,
	...props
}) => {
	const editor = useTiptapEditor();
	const resource = useResourceContext();

	const onClick = () => {
		if (!isActivated) {
			setIsActivated(() => true);

			const formattedCode = prettier.format(editor.getHTML(), {
				parser: "html",
				plugins: [parserHtml],
				tabWidth: 2,
				useTabs: true,
			});

			const updateListener = EditorView.updateListener.of((vu) => {
				editor.commands.setContent(vu.state.sliceDoc());
			});

			const codeMirrorState = EditorState.create({
				doc: formattedCode,
				extensions: [
					basicSetup,
					html(),
					keymap.of([indentWithTab]),
					drawSelection(),
					EditorView.lineWrapping,
					updateListener,
				],
			});

			const codeMirrorView = new EditorView({
				state: codeMirrorState,
				parent: textAreaRef.current,
			});

			setCodeMirrorInstance(codeMirrorView);
			editor?.setOptions({
				editable: false,
			});
		} else {
			setIsActivated(() => false);

			codeMirrorInstance?.destroy?.();
			setCodeMirrorInstance(null);
			editor?.setOptions({
				editable: true,
			});
		}
	};

	return (
		<ToggleButton
			aria-label={label}
			title={label}
			{...props}
			value="small"
			onClick={onClick}
			style={{ padding: "0px 3px" }}
		>
			<Html color={isActivated ? "primary" : "inherit"} />
		</ToggleButton>
	);
};

const MyRichTextInput = ({ size = "small", ...props }) => {
	const resource = useResourceContext();
	const editor = useTiptapEditor();
	const notify = useNotify();

	const handleDrop = (view, event, slice, moved) => {
		if (!moved && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
			let file = event.dataTransfer.files[0]; // the dropped file
			let filesize = (file.size / 1024 / 1024).toFixed(4); // get the filesize in MB

			if (!file.type.startsWith("image/")) {
				window.alert(`Type de fichier non supporté. Utilisé uniquement: jpg, jpeg, png`);
				return;
			}

			if (filesize >= 10) {
				window.alert(
					`Image trop volumineuse (> 10Mo). Veuillez la compresser ou la réduire de taille.`
				);
				return;
			}

			// check valid image type under 10MB
			// check the dimensions
			let _URL = window.URL || window.webkitURL;
			let img = new Image(); /* global Image */
			img.src = _URL.createObjectURL(file);
			img.onload = function () {
				if (this.width > 5000 || this.height > 5000) {
					window.alert("Your images need to be less than 5000 pixels in height and width."); // display alert
					return;
				}

				uploadAndInsertImage(resource, file, notify, editor, view, {
					x: event.clientX,
					y: event.clientY,
				});
			};

			return true; // handled
		}

		return;
	};

	const textAreaRef = useRef();
	const [codeMirrorInstance, setCodeMirrorInstance] = useState(null);
	const [isActivated, setIsActivated] = useState(false);

	return (
		<>
			<RichTextInput
				fullWidth
				toolbar={
					<RichTextInputToolbar>
						<LevelSelect size={size} />
						<FormatButtons size={size} />
						<SmallButtons size={size} />
						<ListButtons size={size} />
						<LinkButtons size={size} />
						{/* <AlignmentButtons size={size} /> */}
						<ImageButtons size={size} />
						<UploadImageButtons size={size} />
						<ClearButtons size={size} />
						<QuoteButtons size={size} />
						<CodeViewButtons
							size={size}
							textAreaRef={textAreaRef}
							isActivated={isActivated}
							setIsActivated={setIsActivated}
							codeMirrorInstance={codeMirrorInstance}
							setCodeMirrorInstance={setCodeMirrorInstance}
						/>
						<VideoButtons size={size} />
					</RichTextInputToolbar>
				}
				editorOptions={{
					...DefaultEditorOptions,
					extensions: [...DefaultEditorOptions.extensions, Small, Iframe],
					editorProps: {
						handleDrop,
						attributes: {
							class: isActivated ? "hidden" : "",
						},
					},
				}}
				readOnly={isActivated}
				label="Body"
				source="body"
				{...props}
			/>
			<div style={{ width: "100%" }} ref={textAreaRef}></div>
		</>
	);
};

export default MyRichTextInput;
